import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Printer, ChevronLeft, ChevronRight, Loader2, ShieldCheck, ShieldAlert, ShieldX, MapPin, CalendarClock } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { isLeader } from "@/lib/permissions";

/** Print-friendly shift-log report grouped by name over a week/month/custom
 * range. Users pick a preset (This week / Last week / This month / Last
 * month) or a custom range, then hit "Print". A dedicated print stylesheet
 * kicks in so browsers save to PDF cleanly.
 */
export default function ShiftLogReport() {
  const { user } = useAuth();

  const today = todayMstIso();
  const [startDate, setStartDate] = useState(startOfWeekMst(today));
  const [endDate, setEndDate] = useState(endOfWeekMst(today));
  const [preset, setPreset] = useState("this-week");
  const [rows, setRows] = useState([]);
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const bag = [
        r.user.display_name, r.user.employee_number,
        r.user.department, r.user.job_title,
        r.city?.code, r.city?.name,
      ].filter(Boolean).join(" ").toLowerCase();
      return bag.includes(q);
    });
  }, [rows, query]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/team/shift-log/range?start_date=${startDate}&end_date=${endDate}`);
      setRows(data.rows || []);
      setDays(data.days || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load shift-log range");
      setRows([]); setDays([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [query, startDate, endDate]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);

  const setPresetRange = (key) => {
    setPreset(key);
    const now = todayMstIso();
    if (key === "this-week")  { setStartDate(startOfWeekMst(now)); setEndDate(endOfWeekMst(now)); }
    if (key === "last-week")  { const s = startOfWeekMst(now, -1); setStartDate(s); setEndDate(endOfWeekMst(now, -1)); }
    if (key === "this-month") { setStartDate(startOfMonth(now)); setEndDate(endOfMonth(now)); }
    if (key === "last-month") { setStartDate(startOfMonth(now, -1)); setEndDate(endOfMonth(now, -1)); }
  };

  const rangeLabel = useMemo(() => {
    if (preset === "this-week") return "This week";
    if (preset === "last-week") return "Last week";
    if (preset === "this-month") return "This month";
    if (preset === "last-month") return "Last month";
    return "Custom";
  }, [preset]);

  if (!isLeader(user) && user?.role !== "crew_chief") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <ShieldAlert className="w-8 h-8 mx-auto text-gray-400" />
        <h1 className="mt-3 font-display text-2xl font-semibold">Leadership only</h1>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-10 shift-log-report">
      {/* SCREEN-ONLY: controls */}
      <div className="print:hidden">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Team · Shift Log · Report · MST</div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-1">Shift log — weekly / monthly report</h1>
            <p className="text-sm text-gray-500 mt-1">Grouped by name. Pick a range, then hit <span className="font-medium">Print</span> to save as PDF.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/team/shift-log">
              <Button variant="outline" data-testid="shift-report-back"><ChevronLeft className="w-4 h-4 mr-1" /> Back to daily view</Button>
            </Link>
            <Button
              onClick={() => window.print()}
              disabled={loading}
              className="bg-summit-primary hover:bg-summit-primary-soft text-white"
              data-testid="shift-report-print"
            >
              <Printer className="w-4 h-4 mr-1.5" /> Print / Save PDF
            </Button>
          </div>
        </div>

        <div className="mt-6 border border-summit-hairline rounded-lg bg-white p-4">
          <div className="flex flex-wrap gap-2">
            {[
              { key: "this-week", label: "This week" },
              { key: "last-week", label: "Last week" },
              { key: "this-month", label: "This month" },
              { key: "last-month", label: "Last month" },
            ].map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPresetRange(p.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest border transition-colors ${
                  preset === p.key
                    ? "bg-summit-primary text-white border-summit-primary"
                    : "bg-white text-gray-700 border-summit-hairline hover:bg-summit-glacier"
                }`}
                data-testid={`shift-report-preset-${p.key}`}
              >{p.label}</button>
            ))}
            <div className="flex-1 min-w-[240px]" />
            <div className="flex items-center gap-2">
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Start</div>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPreset("custom"); }}
                className="w-40"
                data-testid="shift-report-start"
              />
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">End</div>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPreset("custom"); }}
                className="w-40"
                data-testid="shift-report-end"
              />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Filter</div>
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, employee #, department or city — only matches will be printed"
              className="max-w-md"
              data-testid="shift-report-search"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="text-xs font-mono uppercase tracking-widest text-gray-500 hover:text-summit-primary"
                data-testid="shift-report-search-clear"
              >Clear</button>
            )}
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400 ml-auto">
              Showing {filteredRows.length} of {rows.length}
            </div>
          </div>
        </div>
      </div>

      {/* PRINT + SCREEN: report body */}
      <div className="mt-6" data-testid="shift-report-body">
        <div className="mb-6 pb-4 border-b border-summit-hairline">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">SA Tracker · Shift-log report</div>
          <div className="font-display text-2xl font-semibold tracking-tight mt-1">{rangeLabel} · {friendlyRange(startDate, endDate)}</div>
          <div className="text-sm text-gray-500 mt-1">
            Generated {new Date().toLocaleString("en-CA", { timeZone: "America/Edmonton" })} MST · {filteredRows.length} user{filteredRows.length === 1 ? "" : "s"} shown{query ? ` (filtered from ${rows.length})` : ""}
          </div>
          {query && (
            <div className="text-xs text-summit-primary font-mono mt-1">
              Filter: <span className="font-medium">{query}</span>
            </div>
          )}
        </div>

        {loading && (
          <div className="text-center py-10 text-gray-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading…
          </div>
        )}

        {!loading && filteredRows.length === 0 && (
          <div className="text-center py-10 text-gray-400 text-sm">
            {query ? `No users match "${query}".` : "No direct reports found for this range."}
          </div>
        )}

        {!loading && filteredRows.map((row, idx) => {
          const onPage = idx >= safePage * PAGE_SIZE && idx < (safePage + 1) * PAGE_SIZE;
          return (
          <div
            key={row.user.id}
            className={`mb-6 break-inside-avoid shift-report-user-block ${(idx > 0 && idx % 10 === 0) ? "shift-report-page-break" : ""} ${onPage ? "" : "hidden print:block"}`}
            data-testid={`shift-report-user-${row.user.employee_number}`}
          >
            <div className="flex items-baseline justify-between border-b border-gray-300 pb-1 mb-2">
              <div>
                <div className="font-display text-lg font-semibold tracking-tight">{row.user.display_name}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                  #{row.user.employee_number} · {row.user.job_title || "—"} · {row.user.department || "—"}
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Base city</div>
                <div className="font-mono text-sm">{row.city.code || "—"}{row.city.name ? ` · ${row.city.name}` : ""}{row.city.geofence_configured ? ` · ${row.city.radius_km} km` : ""}</div>
              </div>
            </div>
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-1 pr-3 font-mono text-[10px] uppercase tracking-widest">Date (MST)</th>
                  <th className="py-1 pr-3 font-mono text-[10px] uppercase tracking-widest">Duty start</th>
                  <th className="py-1 pr-3 font-mono text-[10px] uppercase tracking-widest">Aircraft</th>
                  <th className="py-1 pr-3 font-mono text-[10px] uppercase tracking-widest">Task type</th>
                  <th className="py-1 pr-3 font-mono text-[10px] uppercase tracking-widest">Distance</th>
                  <th className="py-1 pr-3 font-mono text-[10px] uppercase tracking-widest">GPS</th>
                </tr>
              </thead>
              <tbody>
                {row.days.map((d) => (
                  <React.Fragment key={d.date}>
                    <ReportDayRow date={d.date} entry={d} radiusKm={row.city.radius_km} />
                    {(d.flagged_entries || []).map((fe) => (
                      <ReportFlaggedRow key={fe.id} date={d.date} entry={fe} radiusKm={row.city.radius_km} />
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          );
        })}

        {/* SCREEN-ONLY pagination — 10 users per page. Print always includes
            every user (hidden blocks flip back on via print:block). */}
        {!loading && filteredRows.length > PAGE_SIZE && (
          <div className="print:hidden mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-summit-hairline pt-4" data-testid="shift-report-pagination">
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
              Showing users {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, filteredRows.length)} of {filteredRows.length} · print includes all
            </div>
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.max(0, safePage - 1))}
                disabled={safePage === 0}
                data-testid="shift-report-page-prev"
              ><ChevronLeft className="w-4 h-4" /> Prev</Button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPage(i)}
                  className={`w-8 h-8 rounded-md text-xs font-mono border transition-colors ${
                    i === safePage
                      ? "bg-summit-primary text-white border-summit-primary"
                      : "bg-white text-gray-700 border-summit-hairline hover:bg-summit-glacier"
                  }`}
                  data-testid={`shift-report-page-${i + 1}`}
                >{i + 1}</button>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
                disabled={safePage >= totalPages - 1}
                data-testid="shift-report-page-next"
              >Next <ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </div>

      {/* Print stylesheet — collapses screen chrome, tightens the grid so it
          fits landscape US Letter / A4. Paginates: hard page break every 10
          users (`shift-report-page-break` marker inserted at the 11th, 21st…
          user block). */}
      <style>{`
        @media print {
          @page { size: landscape; margin: 12mm; }
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .shift-log-report { max-width: none !important; padding: 0 !important; }
          nav, footer, header { display: none !important; }
          .print\\:hidden { display: none !important; }
          .break-inside-avoid { break-inside: avoid; page-break-inside: avoid; }
          .shift-report-page-break { break-before: page; page-break-before: always; }
          table { font-size: 10px; }
        }
      `}</style>
    </div>
  );
}

function ReportDayRow({ date, entry, radiusKm }) {
  const s = entry.session;
  const [y, m, d] = date.split("-");
  const dt = new Date(`${date}T12:00:00Z`);
  const weekday = new Intl.DateTimeFormat("en-US", { timeZone: "America/Edmonton", weekday: "short" }).format(dt);
  const isDuty = s?.kind === "duty";

  const timeLabel = s
    ? new Date(s.started_at_iso).toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit", timeZone: "America/Edmonton" })
    : "—";

  let statusBadge = null;
  if (!s) statusBadge = "—";
  else if (entry.geofence_status === "inside") statusBadge = <span className="inline-flex items-center gap-1 text-emerald-700"><ShieldCheck className="w-3 h-3" /> Inside</span>;
  else if (entry.geofence_status === "outside") statusBadge = <span className="inline-flex items-center gap-1 text-rose-700"><ShieldAlert className="w-3 h-3" /> Outside</span>;
  else if (entry.geofence_status === "denied") statusBadge = <span className="inline-flex items-center gap-1 text-amber-700"><ShieldX className="w-3 h-3" /> Denied</span>;
  else if (entry.geofence_status === "unavailable") statusBadge = <span className="inline-flex items-center gap-1 text-amber-700"><ShieldX className="w-3 h-3" /> Unavail</span>;
  else if (entry.geofence_status === "unsupported") statusBadge = <span className="inline-flex items-center gap-1 text-slate-700"><ShieldX className="w-3 h-3" /> Unsup</span>;
  else if (entry.geofence_status === "not-configured") statusBadge = <span className="inline-flex items-center gap-1 text-slate-500"><MapPin className="w-3 h-3" /> N/A</span>;
  else statusBadge = <span className="text-gray-400">—</span>;

  const distanceLabel = (() => {
    if (!s || s.distance_km == null) return "—";
    const km = s.distance_km;
    const inside = km <= (radiusKm || 1);
    return <span className={`font-mono ${inside ? "text-emerald-700" : "text-rose-700"}`}>{km.toFixed(km >= 10 ? 1 : 2)} km</span>;
  })();

  return (
    <tr className="border-t border-gray-200">
      <td className="py-1 pr-3 font-mono tabular-nums">{weekday}, {m}/{d}</td>
      <td className="py-1 pr-3 font-mono tabular-nums">{timeLabel}</td>
      <td className="py-1 pr-3 font-mono">
        {isDuty ? <span className="text-emerald-700 font-medium">DUTY</span> : (s?.aircraft_tail || "—")}
      </td>
      <td className="py-1 pr-3">
        {isDuty ? (
          <span>Start of duty{s?.auto_closed ? <span className="ml-1 text-[9px] text-slate-500">· auto-closed</span> : null}</span>
        ) : (
          <>
            {s?.task_type_name || (s ? "—" : <span className="text-gray-400">not on duty</span>)}
            {s?.is_capex && s?.capex_code_name && (
              <span className="ml-1 font-mono text-[9px] text-emerald-700">· AV: {s.capex_code_name}</span>
            )}
            {s?.is_ot && <span className="ml-1 font-mono text-[9px] text-amber-700">· OT</span>}
            {s?.is_work_on_stat && <span className="ml-1 font-mono text-[9px] text-rose-700">· STAT</span>}
          </>
        )}
      </td>
      <td className="py-1 pr-3">{distanceLabel}</td>
      <td className="py-1 pr-3">{statusBadge}</td>
    </tr>
  );
}

function ReportFlaggedRow({ date, entry, radiusKm }) {
  const [y, m, d] = date.split("-");
  const isDuty = entry.kind === "duty";
  const isDutyStop = entry.kind === "duty_stop";
  const isStop = entry.kind === "clock_stop";
  const isPing = entry.kind === "ping";
  const neutral = isDutyStop || (isDuty && entry.geofence_status !== "outside");
  const timeLabel = new Date(entry.started_at_iso).toLocaleTimeString(
    "en-CA", { hour: "2-digit", minute: "2-digit", timeZone: "America/Edmonton" }
  );
  const km = entry.distance_km;
  const rowLabel = isDutyStop ? "↳ stop duty" : isDuty ? "↳ duty start" : isStop ? "↳ clock-out" : isPing ? "↳ ping" : "↳ clock-in";
  return (
    <tr className={neutral ? "border-t border-indigo-200 bg-indigo-50/50" : "border-t border-rose-200 bg-rose-50/60"}>
      <td className={`py-1 pr-3 pl-3 text-[10px] font-mono ${neutral ? "text-indigo-700" : "text-rose-700"}`}>{rowLabel}</td>
      <td className="py-1 pr-3 font-mono tabular-nums">{timeLabel}</td>
      <td className="py-1 pr-3 font-mono">
        {isDuty || isDutyStop ? <span className="text-emerald-700 font-medium">DUTY</span> : (entry.aircraft_tail || "—")}
      </td>
      <td className="py-1 pr-3">
        {isDutyStop ? (
          <span>End of shift{entry.auto_closed ? <span className="ml-1 text-[9px] text-slate-500">· auto-closed</span> : null}</span>
        ) : isStop ? "Clock-out" : isPing ? "Location ping" : isDuty ? "Start of duty" : (entry.task_type_name || "—")}
      </td>
      <td className="py-1 pr-3">
        {km != null && (
          <span className={`font-mono ${neutral ? "text-gray-600" : "text-rose-700"}`}>{km.toFixed(km >= 10 ? 1 : 2)} km</span>
        )}
      </td>
      <td className="py-1 pr-3">
        {neutral ? (
          <span className="inline-flex items-center gap-1 text-indigo-700"><ShieldCheck className="w-3 h-3" /> Stamp</span>
        ) : (
          <span className="inline-flex items-center gap-1 text-rose-700"><ShieldAlert className="w-3 h-3" /> Outside</span>
        )}
      </td>
    </tr>
  );
}

// -------- MST date math --------
function todayMstIso() {
  return new Date(new Date().toLocaleString("en-US", { timeZone: "America/Edmonton" }))
    .toISOString().slice(0, 10);
}
function addDaysIso(iso, days) {
  const d = new Date(iso + "T12:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
function startOfWeekMst(iso, weekOffset = 0) {
  // Week starts Monday
  const d = new Date(iso + "T12:00:00");
  const day = d.getDay(); // 0 = Sun … 6 = Sat
  const daysToMonday = (day + 6) % 7;
  d.setDate(d.getDate() - daysToMonday + weekOffset * 7);
  return d.toISOString().slice(0, 10);
}
function endOfWeekMst(iso, weekOffset = 0) {
  return addDaysIso(startOfWeekMst(iso, weekOffset), 6);
}
function startOfMonth(iso, monthOffset = 0) {
  const d = new Date(iso + "T12:00:00");
  d.setDate(1);
  d.setMonth(d.getMonth() + monthOffset);
  return d.toISOString().slice(0, 10);
}
function endOfMonth(iso, monthOffset = 0) {
  const d = new Date(iso + "T12:00:00");
  d.setDate(1);
  d.setMonth(d.getMonth() + monthOffset + 1);
  d.setDate(0);
  return d.toISOString().slice(0, 10);
}
function friendlyRange(a, b) {
  const opts = { month: "short", day: "numeric", year: "numeric", timeZone: "America/Edmonton" };
  const s = new Date(a + "T12:00:00").toLocaleDateString("en-US", opts);
  const e = new Date(b + "T12:00:00").toLocaleDateString("en-US", opts);
  return `${s} – ${e}`;
}
